import { DeepOmit } from '../../lib/helpers/apollo';
import { gql } from '@apollo/client';
import { DropResult } from '@hello-pangea/dnd';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
	InvoiceStatus,
	LineItemDataInput,
	UseLineItemsInvoiceFragment,
	UseLineItemsInvoiceEditLineItemFragment,
} from '#types';

export const useLineItemsInvoiceEditLineItemFragment = gql`
	fragment useLineItemsInvoiceEditLineItem on LineItem {
		id
		name
		quantity
		rate
		taxPercentage
		notes
	}
`;

export const useLineItemsInvoiceFragment = gql`
	fragment useLineItemsInvoice on Invoice {
		id
		status
		lineItems {
			...useLineItemsInvoiceEditLineItem
		}
	}
	${ useLineItemsInvoiceEditLineItemFragment }
`;

const reorder = <T>( list: T[], startIndex: number, endIndex: number ): T[] => {
	// using Array.from here to cover instances when the "list" is coming out of Apollo as readonly
	const result = Array.from( list );
	const [ removed ] = result.splice( startIndex, 1 );
	result.splice( endIndex, 0, removed );

	return result;
};

export type LineItemDetails = {
	id: string;
	disabled: boolean;
	loading: boolean;
	autoFocus: boolean;
	validation: Record<
		keyof DeepOmit<
			UseLineItemsInvoiceEditLineItemFragment,
			'__typename' | 'id' | 'notes'
		>,
		string
	>;
};

type LineItemNumericValues = 'rate' | 'quantity' | 'taxPercentage';
const lineItemNumericValuesArray: Array<LineItemNumericValues> = [
	'rate',
	'quantity',
	'taxPercentage',
];

export type OnUpdate = (
	delta: Partial<UseLineItemsInvoiceEditLineItemFragment>
) => void;

const newItemIdMarker = 'new-line-item';
let arbitraryCounter = 0;

export const getNewItemId = ( index?: number, setArbitraryCounter?: number ) => {
	if ( typeof index === 'number' && typeof setArbitraryCounter === 'number' ) {
		arbitraryCounter = setArbitraryCounter;
	}
	return `${ newItemIdMarker }-${
		typeof index === 'number' ? index : arbitraryCounter++
	}`;
};

const getEmptyLineItem = (): UseLineItemsInvoiceEditLineItemFragment => ( {
	id: getNewItemId(),
	name: '',
	quantity: 1,
	rate: 0,
	taxPercentage: 0,
	notes: '',
} );

const getEmptyLineItemDetails = ( id: string ): LineItemDetails => ( {
	id,
	autoFocus: false,
	disabled: false,
	loading: false,
	validation: {
		name: '',
		quantity: '',
		rate: '',
		taxPercentage: '',
	},
} );

export const useLineItems = ( {
	invoiceId,
	invoice,
	setInvoice,
}: {
	invoiceId?: string;
	invoice?: Omit<UseLineItemsInvoiceFragment, 'id'> & { id?: string };
	setInvoice: React.Dispatch<React.SetStateAction<UseLineItemsInvoiceFragment>>;
} ) => {
	// Reset the counter on revisit/reload
	useEffect( () => {
		arbitraryCounter = 0;
	}, [] );

	const [ lineItemsDetails, setLineItemsDetails ] = useState<LineItemDetails[]>(
		[]
	);

	const invoiceLineItems = useMemo( () => invoice?.lineItems, [ invoice ] );
	const invoiceStatus = useMemo( () => invoice?.status, [ invoice ] );
	const invoiceExists = !!invoice?.id;

	// setLineItems is a wrapper that does the same as React.Dispatch<React.SetStateAction<UseLineItemsInvoiceFragment>> but for line items only
	const setLineItems = useCallback(
		(
			props:
				| ( (
						prevState: UseLineItemsInvoiceEditLineItemFragment[]
				  ) => UseLineItemsInvoiceEditLineItemFragment[] )
				| UseLineItemsInvoiceEditLineItemFragment[]
		) => {
			setLineItemsDetails( ( prevState ) =>
				prevState.map( ( lineItemDetails ) => ( {
					...lineItemDetails,
					loading: true,
				} ) )
			);
			if ( typeof props === 'function' ) {
				setInvoice( ( prevState ) => ( {
					...prevState,
					lineItems: props( prevState?.lineItems ),
				} ) );
			} else {
				setInvoice( ( prevState ) => ( {
					...prevState,
					lineItems: props,
				} ) );
			}
			setLineItemsDetails( ( prevState ) =>
				prevState.map( ( lineItemDetails ) => ( {
					...lineItemDetails,
					loading: false,
				} ) )
			);
		},
		[ setInvoice ]
	);

	// For brand-new invoices only, if the line items array is not yet defined,
	// bootstrap it with a single empty item. During /invoice/create -> /invoice/[id]
	// navigation in App Router, invoice state can be temporarily undefined while the
	// detail query hydrates; we must not overwrite the fetched server line items with
	// a synthetic empty item in that case.
	useEffect( () => {
		if ( !invoiceLineItems ) {
			if ( !invoiceId ) {
				const emptyLineItem = getEmptyLineItem();
				setLineItems( [ emptyLineItem ] );
				setLineItemsDetails( [ getEmptyLineItemDetails( emptyLineItem.id ) ] );
			}
		} else {
			setLineItemsDetails(
				invoiceLineItems.map( ( lineItem ) => getEmptyLineItemDetails( lineItem.id ) )
			);
		}
	}, [
		invoiceId,
		invoiceExists,
		invoiceLineItems,
		setLineItems
	] );

	const onDragEnd = useCallback(
		( { destination, source }: DropResult ) => {
			// dropped outside the list
			if ( destination && invoice ) {
				setLineItems( ( prevState ) =>
					reorder( prevState, source.index, destination.index )
				);
				const newItemsDetails = reorder(
					lineItemsDetails,
					source.index,
					destination.index
				);
				setLineItemsDetails( newItemsDetails );
			}
		},
		[
			invoice,
			lineItemsDetails,
			setLineItems
		]
	);

	const onAddLineItem = useCallback( () => {
		const newLineItem = getEmptyLineItem();
		const newLineItemDetails = getEmptyLineItemDetails( newLineItem.id );
		newLineItemDetails.autoFocus = true;
		setLineItems( ( prevState ) => [ ...prevState, newLineItem ] );
		setLineItemsDetails( ( prevState ) => [ ...prevState, newLineItemDetails ] );
	}, [ setLineItems, setLineItemsDetails ] );

	const onRemoveLineItem = useCallback(
		( id: string ) => {
			const newLineItem = getEmptyLineItem();
			setLineItems( ( prevState ) =>
				// there should always be one line item in the array
				prevState.filter( ( lineItem ) => lineItem.id !== id ).length === 0
					? [ newLineItem ]
					: prevState.filter( ( lineItem ) => lineItem.id !== id )
			);
			setLineItemsDetails( ( prevState ) =>
				prevState.filter( ( lineItemDetails ) => lineItemDetails.id !== id )
					.length === 0
					? [ getEmptyLineItemDetails( newLineItem.id ) ]
					: prevState.filter( ( lineItemDetails ) => lineItemDetails.id !== id )
			);
		},
		[ setLineItems ]
	);

	// Strip out the "fake" ids, the ones we use for currently-client-sid-only items
	const lineItemsInput: LineItemDataInput[] = useMemo(
		() =>
			invoiceLineItems?.map( ( rawLineItem, order ) => {
				const { __typename, ...lineItem } = rawLineItem;
				const { id, ...restOfLineItem } = lineItem;
				if ( id.startsWith( newItemIdMarker ) ) {
					return { ...restOfLineItem, order };
				} else {
					return { ...lineItem, order };
				}
			} ) || [],
		[ invoiceLineItems ]
	);

	/* we need to remember our last "intent", like, did we last try to save?
	if so, keep using that validation. */
	const lastValidationIntentRef = useRef( false );
	// Save the information that we tried to save or send so we know to validate on each key press
	const validationEnabledRef = useRef( false );

	const validate = useCallback(
		( intendToSend?: boolean ) => {
			if ( typeof intendToSend === 'boolean' ) {
				lastValidationIntentRef.current = intendToSend;
				validationEnabledRef.current = true;
			}
			// if a save or send button hasn't been clicked yet, don't do this validation, just bail out
			if ( !validationEnabledRef.current ) {
				return true;
			}
			const updateLineItemValidation = (
				id: string,
				field: keyof LineItemDetails['validation'],
				value: string
			) =>
				setLineItemsDetails( ( prevState ) =>
					prevState.map( ( lineItemDetails ) =>
						lineItemDetails.id === id
							? {
								...lineItemDetails,
								validation: { ...lineItemDetails.validation, [ field ]: value },
							  }
							: lineItemDetails
					)
				);
			if (
				intendToSend ||
				lastValidationIntentRef.current ||
				( invoiceStatus && invoiceStatus !== InvoiceStatus.Draft )
			) {
				const nameCheck = ( lineItem: UseLineItemsInvoiceEditLineItemFragment ) =>
					!!lineItem.name;
				// if we intend to send this invoice, then all line items need to have a name
				invoiceLineItems?.forEach( ( lineItem ) =>
					updateLineItemValidation(
						lineItem.id,
						'name',
						!nameCheck( lineItem )
							? 'Name is required in order to send invoices'
							: ''
					)
				);
				const allLineItemsHaveName = invoiceLineItems?.every( nameCheck );
				if ( !allLineItemsHaveName ) {
					return false;
				}
			} else {
				// make sure we clear validation if necessary
				invoiceLineItems?.forEach( ( lineItem ) =>
					updateLineItemValidation( lineItem.id, 'name', '' )
				);
			}
			const nonNegativeCheck = (
				lineItem: UseLineItemsInvoiceEditLineItemFragment,
				field: LineItemNumericValues
			) => ( typeof lineItem[ field ] === 'number' && lineItem[ field ] ) || 0 >= 0;
			invoiceLineItems?.forEach( ( lineItem ) =>
				lineItemNumericValuesArray.forEach( ( field ) =>
					updateLineItemValidation(
						lineItem.id,
						field,
						!nonNegativeCheck( lineItem, field )
							? `A non-negative ${ field } is required`
							: ''
					)
				)
			);
			const allNumericValuesArePositive = lineItemNumericValuesArray.every(
				( field ) =>
					invoiceLineItems?.every( ( lineItem ) =>
						nonNegativeCheck( lineItem, field )
					)
			);
			if ( !allNumericValuesArePositive ) {
				return false;
			}
			return true;
		},
		[ invoiceLineItems, invoiceStatus ]
	);

	// because of the render cycle, we need to wait one cycle for line items to update so we can validate
	const [ validateOnNextRenderCycle, setValidateOnNextRenderCycle ] =
		useState( false );
	useEffect( () => {
		if ( validateOnNextRenderCycle ) {
			setValidateOnNextRenderCycle( false );
			validate();
		}
	}, [ validate, validateOnNextRenderCycle ] );

	const onUpdateLineItem = useCallback(
		( id: string ): OnUpdate =>
			( delta ) => {
				setLineItems( ( prevState ) =>
					prevState.map( ( lineItem ) =>
						lineItem.id === id ? { ...lineItem, ...delta } : lineItem
					)
				);
			},
		[ setLineItems ]
	);

	return {
		lineItemsDetails,
		onDragEnd,
		onAddLineItem,
		onRemoveLineItem,
		onUpdateLineItem,
		lineItemsInput,
		validate,
		onBlur: () => setValidateOnNextRenderCycle( true ),
		// replaceLineItems is used with applying invoice templates only
		replaceLineItems: (
			newLineItems?: UseLineItemsInvoiceEditLineItemFragment[]
		) => {
			if ( newLineItems ) {
				/* not setting line items here because we want to avoid a render-loop, so we set them
				when we apply the template in useInvoiceEdit */
				setLineItemsDetails(
					newLineItems?.map( ( li ) => getEmptyLineItemDetails( li.id ) ) || []
				);
			} else {
				const newLineItem = getEmptyLineItem();
				setLineItemsDetails( [ getEmptyLineItemDetails( newLineItem.id ) ] );
			}
		},
	};
};

export type UseLineItems = ReturnType<typeof useLineItems>;
